import { generateId } from "./Utils";
import { performFullMigration, validateCollectionFormat } from "./migrationUtils";
import { saveToStorage, loadFromStorage, removeFromStorage, STORAGE_KEYS } from "./storageUtils";
import { Folder, Collection } from "../types";
import { sanitizeElementTree } from "./treeUtils";

const DEFAULT_FOLDER_NAME = "General";
const DEFAULT_COLLECTION_NAME = "Inbox";

export const migrateUnfiledLists = (folders: Folder[], unfiledLists: Collection[]): Folder[] => {
    const defaultFolder = folders.find(f => f.name === DEFAULT_FOLDER_NAME);
    
    if (unfiledLists.length === 0) return folders;
    
    if (defaultFolder) {
        folders = folders.map(f => 
            f.id === defaultFolder.id 
                ? { ...f, collections: [...(f.collections || []), ...unfiledLists] }
                : f
        );
    } else {
        const newDefaultFolder: Folder = { 
            id: generateId(), 
            name: DEFAULT_FOLDER_NAME, 
            collections: unfiledLists 
        };
        folders = [...folders, newDefaultFolder];
    }
    
    saveToStorage(STORAGE_KEYS.FOLDERS, folders);
    removeFromStorage(STORAGE_KEYS.LEGACY_LISTS);
    
    return folders;
};

export const deduplicateFolderCollections = (folders: Folder[]): Folder[] => {
    return folders.map(folder => ({
        ...folder,
        collections: (folder.collections || []).filter((collection, index, self) => 
            index === self.findIndex(c => c.id === collection.id)
        )
    }));
};

export const ensureDefaultFolder = (folders: Folder[]): Folder[] => {
    const defaultFolder = folders.find(f => f.name === DEFAULT_FOLDER_NAME);
    if (defaultFolder) return folders;
    
    return [...folders, { 
        id: generateId(), 
        name: DEFAULT_FOLDER_NAME, 
        collections: [] 
    }];
};

export const ensureDefaultCollection = (folders: Folder[]): Folder[] => {
    const hasAnyCollections = folders.some(folder => folder.collections && folder.collections.length > 0);
    if (hasAnyCollections) return folders;
    
    const inboxCollection: Collection = { 
        id: generateId(), 
        name: DEFAULT_COLLECTION_NAME, 
        elements: [],
        visibleTypes: ['task'] // Default to tasks only
    };
    return folders.map(folder => 
        folder.name === DEFAULT_FOLDER_NAME 
            ? { ...folder, collections: [inboxCollection] }
            : folder
    );
};

export const ensureInboxCollection = (folders: Folder[]): Folder[] => {
    const hasInbox = folders.some(folder =>
        (folder.collections || []).some(collection => collection.name === DEFAULT_COLLECTION_NAME)
    );
    if (hasInbox) return folders;

    const inboxCollection: Collection = {
        id: generateId(),
        name: DEFAULT_COLLECTION_NAME,
        elements: [],
        visibleTypes: ['task']
    };
    const targetFolderId = folders.find(folder => folder.name === DEFAULT_FOLDER_NAME)?.id || folders[0]?.id;

    return folders.map((folder) =>
        folder.id === targetFolderId
            ? { ...folder, collections: [inboxCollection, ...(folder.collections || [])] }
            : folder
    );
};

export const getFirstCollectionId = (folders: Folder[]): string | null => {
    for (const folder of folders) {
        if (folder.collections && folder.collections.length > 0) {
            return folder.collections[0].id;
        }
    }
    return null;
};

export const sanitizeFolders = (folders: Folder[]): Folder[] => {
    let foldersChanged = false;
    const cleanedFolders = folders.map((folder) => {
        let collectionsChanged = false;
        const cleanedCollections = (folder.collections || []).map((collection) => {
            const cleanedElements = sanitizeElementTree(collection.elements || [], true);
            if (cleanedElements === collection.elements) return collection;

            collectionsChanged = true;
            return { ...collection, elements: cleanedElements };
        });

        if (!collectionsChanged) return folder;
        foldersChanged = true;
        return { ...folder, collections: cleanedCollections };
    });

    return foldersChanged ? cleanedFolders : folders;
};

export const initializeFolders = (): Folder[] => {
    let folders = loadFromStorage<Folder[]>(STORAGE_KEYS.FOLDERS, []) || [];
    const unfiledLists = loadFromStorage<Collection[]>(STORAGE_KEYS.LEGACY_LISTS, []) || [];
    
    // Check if migration is needed
    if (!validateCollectionFormat(folders) || unfiledLists.length > 0) {
        const migrated = performFullMigration();
        folders = (migrated as { folders: Folder[] }).folders || [];
        folders = ensureDefaultFolder(folders);
        folders = ensureDefaultCollection(folders);
        folders = ensureInboxCollection(folders);
        return sanitizeFolders(folders);
    }
    
    folders = migrateUnfiledLists(folders, unfiledLists);
    folders = deduplicateFolderCollections(folders);
    folders = ensureDefaultFolder(folders);
    folders = ensureDefaultCollection(folders);
    folders = ensureInboxCollection(folders);
    folders = sanitizeFolders(folders);
    
    return folders;
};

export const findCollectionById = (collectionId: string, folderList: Folder[]): Collection | null => {
    for (const folder of folderList) {
        const found = (folder.collections || []).find((collection) => collection.id === collectionId);
        if (found) return found;
    }
    return null;
};

// Legacy alias for backward compatibility
export const findListById = findCollectionById;
